import {forwardRef, type HTMLAttributes, useEffect, useImperativeHandle, useMemo, useRef, useState} from "react";
import TerminalContainerTitleBar, {type TerminalContainerTitleBarRef} from "./TerminalContainerTitleBar.tsx";
import TerminalContainerTabContainer, {
    type TerminalContainerTabContainerRef
} from "./TerminalContainerTabContainer.tsx";
import TerminalContainerContext, {
    TerminalScreenContainerContextImpl,
    TerminalScreenContainerStates,
    type TerminalScreenEventHandlers,
    type TerminalTab
} from "./TerminalContainerContext.ts";
import TerminalContainerXTermPanel from "./TerminalContainerXTermPanel.tsx";
import {useNavigate} from "react-router-dom";
import TerminalSearchDialog, {type TerminalSearchDialogRef} from "./TerminalSearchDialog.tsx";


export interface TerminalScreenContainerRef {
    selectRightTab(): void;

    selectLeftTab(): void;

    selectLastTab(): void;

    selectTab(index: number): void;

    newTab(): void;

    closeActiveTab(): void;

    bindTab(key: string, tabKey: number): void;

    write(tabKey: number, data: string): void;

    setTitle(tabKey: number, title: string): void;

    removeTab(tabKey: number): void;

    removeTabByReqKey(reqKey: string): void;

    pasteToActiveTab(content: string | undefined): void;

    getActiveTabSelection(): string | undefined;

    showSearchDialog(): void;

    closeSearchDialog(): void;
}

export interface TerminalScreenContainerProps extends TerminalScreenEventHandlers, Omit<HTMLAttributes<HTMLDivElement>, 'onResize'> {
    // HTMLAttributes의 onResize는 제거 하고 상속
    instanceId: number,
    statusBarSize: number,
    osImage: string,
}

class TerminalScreenContainerRefImpl implements TerminalScreenContainerRef {
    private readonly _context: TerminalScreenContainerContextImpl;

    constructor(context: TerminalScreenContainerContextImpl) {
        this._context = context;
    }

    selectRightTab(): void {
        const activeTab = this._context.activeTab;
        const currentIdx = this._context.tabs.findIndex((tab) => activeTab == tab);
        const lastIdx = this._context.tabs.length - 1;
        if (currentIdx <= lastIdx) {
            this.selectTab(currentIdx + 1);
        }
    }

    selectLeftTab(): void {
        const activeTab = this._context.activeTab;
        const currentIdx = this._context.tabs.findIndex((tab) => activeTab == tab);
        if (currentIdx > 0) {
            this.selectTab(currentIdx - 1);
        }
    }

    selectTab(index: number): void {
        let terminalTab = this._context.tabs[index];
        if (!terminalTab) {
            terminalTab = this._context.tabs[this._context.tabs.length - 1];
        }
        this._context.activeTab = terminalTab;
    }

    selectLastTab(): void {
        this._context.activeTab = this._context.tabs[this._context.tabs.length - 1];
    }


    newTab(): void {
        this._context.newTab();
    }

    closeActiveTab(): void {
        const tab = this._context.activeTab;
        if (!tab) {
            return
        }

        const tabKey = tab.tabKey
        if (tabKey) {
            this._context.closeTab(tabKey);
        } else {
            this._context.removeTaByReqKey(tab.reqKey);
        }
    }

    removeTab(tabKey: number): void {
        this._context.removeTabByTabKey(tabKey);
    }

    removeTabByReqKey(reqKey: string): void {
        this._context.removeTaByReqKey(reqKey);
    }

    bindTab(key: string, tabKey: number): void {
        this._context.bindTab(key, tabKey);
    }

    write(tabKey: number, data: string): void {
        this._context.writeStdOutByTabKey(tabKey, data);
    }

    setTitle(tabKey: number, title: string): void {
        this._context.setTabTitleByTabKey(tabKey, title);
    }

    pasteToActiveTab(content: string | undefined): void {
        this._context.pasteToActiveTab(content);
    }

    getActiveTabSelection(): string | undefined {
        return this._context.activeTabSelection;
    }

    showSearchDialog(): void {
        this._context.showSearchDialog();
    }

    closeSearchDialog(): void {
        this._context.closeSearchDialog();
    }
}

const TerminalContainer = forwardRef<TerminalScreenContainerRef, TerminalScreenContainerProps>(
    (props, ref) => {
        const navigate = useNavigate();
        const lockState = useState(false);
        const tabsState = useState<TerminalTab[]>([]);
        const activeTabState = useState<TerminalTab>();
        const terminalSearchDialog = useRef<TerminalSearchDialogRef>(null);

        const context = useMemo<TerminalScreenContainerContextImpl>(() => {
            const terminalScreenEventHandlers = props as TerminalScreenEventHandlers;

            return new TerminalScreenContainerContextImpl(
                navigate,
                props.instanceId,
                terminalScreenEventHandlers,
                new TerminalScreenContainerStates(
                    terminalScreenEventHandlers,
                    lockState,
                    tabsState,
                    activeTabState,
                ),
                terminalSearchDialog,
            )
        }, [props, navigate, lockState, tabsState, activeTabState]);
        context.changeThemeSetFromOsImageName(props.osImage);
        const terminalContainerTitleBarRef = useRef<TerminalContainerTitleBarRef>(null);
        const terminalContainerTabContainerRef = useRef<TerminalContainerTabContainerRef>(null);

        useImperativeHandle(ref, () => new TerminalScreenContainerRefImpl(context));

        function handleResize(/*e: Event*/) {
            context.fitActiveTerminal();
        }

        useEffect(() => {
            window.addEventListener('resize', handleResize);

            return () => {
                window.removeEventListener('resize', handleResize);
            }
        });

        function createHeight(): string {
            const terminalContainerTitleBarHeight = terminalContainerTitleBarRef.current?.offsetHeight || 0;
            const terminalContainerTabContainerHeight = terminalContainerTabContainerRef.current?.offsetHeight || 0;

            const totalSubtractPx = props.statusBarSize
                + terminalContainerTitleBarHeight
                + terminalContainerTabContainerHeight;

            // rem=(term-container padding topBottom)+(tab-content paddingTopBottom)
            return `calc( 100vh - ${totalSubtractPx}px - 5rem)`;
        }

        const xtermPanels = context.tabs.map(
            (tab) => <TerminalContainerXTermPanel key={tab.reqKey} tab={tab}/>
        );

        return (
            <TerminalContainerContext.Provider value={context}>
                <div id={props.id ?? "terminal-container"} className={props.className ?? "row"}>
                    <div className="card term-container">

                        <TerminalContainerTitleBar ref={terminalContainerTitleBarRef}/>
                        <TerminalContainerTabContainer ref={terminalContainerTabContainerRef}/>
                        <div className="tab-content p-3 border-top-0" style={{height: createHeight()}}>
                            {xtermPanels}
                        </div>

                    </div>
                </div>

                <TerminalSearchDialog ref={terminalSearchDialog}/>
            </TerminalContainerContext.Provider>
        );
    }
);

export default TerminalContainer;